import {
  EyeInvisibleOutlined,
  EyeOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, Table, TableColumnsType, Tooltip } from 'antd'
import { t } from 'i18next'
import { useState } from 'react'
import { cartManagementApi } from 'src/api/cart-management'
import ConfirmDeleteModal from 'src/components/staff-and-role-management/ConfirmDeleteModal'
import { ITicket } from 'src/interfaces/cart-management'
import { getListCartAction } from 'src/redux/actions/cart-management'
import ReminderModal from './ReminderModal'
import moment from 'moment'
import { useListCartsTable } from './useListCartsTable'

interface IToggle {
  ticketGuid?: string
  isShow?: boolean
}

interface IRemindLoading {
  isRemind?: boolean
  isRemindAll?: boolean
}

export const ExpandedRowRender = ({ data, searchString }: { data: ITicket[], searchString: string }) => {
  const [itemToggle, setItemToggle] = useState<IToggle>({
    ticketGuid: undefined,
    isShow: undefined,
  })
  const [remindLoading, setRemindLoading] = useState<IRemindLoading>({
    isRemind: undefined,
    isRemindAll: undefined,
  })


  const [remindedItem, setRemindedItem] = useState<string>()
  const [cartId, setCartId] = useState<number | undefined>()
  const [isToggleHiding, setIsToggleHiding] = useState(false)
  const dispatch = useAppDispatch()
  const currentPage = useAppSelector((state) => state.carts.currentPage)

  const expandColumns: TableColumnsType<ITicket> = [
    {
      title: 'Ticket',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => {
        return <p className="m-0">{record?.name}</p>
      },
    },
    {
      title: 'Event ID',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => {
        return <p className="m-0">{record?.eventGuid}</p>
      },
    },
    {
      title: 'Event Name',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => {
        return <p className="m-0">{record?.eventName}</p>
      },
    },
    {
      title: 'Event time',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => {
        return (
          <div className="space-y-1">
            <p className="m-0">
              Start: {moment(record?.event?.start).format('DD/MM/YY, HH:mm')}
            </p>
            <p className="m-0">
              End: {moment(record?.event?.end).format('DD/MM/YY, HH:mm')}
            </p>
          </div>
        )
      },
    },
    {
      title: 'Available Stock',
      dataIndex: 'date',
      className: '!text-center',
      key: 'date',
      render: (_, record) => {
        return <p className="m-0">{record?.availableStock}</p>
      },
    },
    {
      title: 'Sold Count',
      dataIndex: 'date',
      className: '!text-center',
      key: 'date',
      render: (_, record) => {
        return <p className="text-center m-0">{record?.soldCount}</p>
      },
    },

    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      // width: '4%',
      title: 'Action',
      render: (_, record) => {
        return (
          <div className="flex justify-center w-full space-x-10">
            {record?.isHide ? (
              <Tooltip title={'Click to show it to users'}>
                <EyeInvisibleOutlined
                  className={`text-lg font-light  cursor-pointer  hover:opacity-80 !text-red-600`}
                  onClick={() => {
                    setItemToggle({
                      ticketGuid: record?.ticketGuid,
                      isShow: true,
                    })
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title={'Click to disable it from users'}>
                <EyeOutlined
                  className={`text-lg font-light  cursor-pointer  hover:opacity-80 `}
                  onClick={() =>
                    setItemToggle({
                      ticketGuid: record?.ticketGuid,
                      isShow: false,
                    })
                  }
                />
              </Tooltip>
            )}
            {record?.isReminder && (
              <Tooltip title={'Remind'}>
                <SendOutlined
                  className={`text-lg font-light  cursor-pointer  hover:opacity-80`}
                  onClick={() => {
                    setCartId(record.id)
                    setRemindedItem(record?.ticketGuid)
                  }}
                />
              </Tooltip>
            )}
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  const handleToggleHide = async () => {
    try {
      if (itemToggle.ticketGuid) {
        setIsToggleHiding(true)
        const res = await cartManagementApi.hideCart(itemToggle.ticketGuid)
        if (res.success) {
          dispatch(getListCartAction({ page: currentPage, search: searchString }))
          message.success({
            content: res?.message ?? t('Change successfully'),
          })
        }
      }
    } catch (error: any) {
      message.error({
        content: error.message,
      })
    } finally {
      setIsToggleHiding(false)
      setItemToggle({
        ticketGuid: undefined,
        isShow: undefined,
      })
    }
  }

  const handleReminder = async (cartTicketId: number | null) => {
    try {
      if (remindedItem) {
        if (cartTicketId) {
          setRemindLoading({
            isRemind: true,
            isRemindAll: false,
          })
        } else {
          setRemindLoading({
            isRemind: false,
            isRemindAll: true,
          })
        }
        const res = await cartManagementApi.reminder(remindedItem, {
          cartTicketId: cartTicketId,
        })
        if (res.success) {
          message.success({
            content: res?.message ?? t('Remind successfully'),
          })
        }
      }
    } catch (error: any) {
      message.error({
        content: error.message,
      })
    } finally {
      setRemindedItem(undefined)
      setCartId(undefined)
      setRemindLoading({
        isRemind: false,
        isRemindAll: false,
      })
    }
  }

  return (
    <>
      <Table<ITicket>
        columns={expandColumns}
        dataSource={data}
        pagination={false}
      />
      <ConfirmDeleteModal
        title={
          itemToggle?.isShow
            ? 'Show this ticket for all users'
            : 'Disable this ticket for all users'
        }
        content={
          itemToggle?.isShow
            ? 'This action will make the ticket available to all users.'
            : 'This action will make the ticket unavailable to all users.'
        }
        open={Boolean(itemToggle.ticketGuid)}
        onClose={() =>
          setItemToggle({
            ticketGuid: undefined,
            isShow: undefined,
          })
        }
        isLoading={isToggleHiding}
        onDelete={handleToggleHide}
      />

      <ReminderModal
        title="Remind users about this ticket"
        content="Note: 'Remind All' will notify all users who have this ticket in their cart."
        open={Boolean(remindedItem)}
        onClose={() => {
          setRemindedItem(undefined)
          setCartId(undefined)
        }}
        isLoading={remindLoading.isRemind}
        isLoadingAll={remindLoading?.isRemindAll}
        onReminderAll={() => handleReminder(null)}
        onReminder={() => handleReminder(cartId as number)}
      />
    </>
  )
}
